import {
    FunctionComponent,
    PropsWithChildren,
    ReactElement,
    use,
    useEffect,
} from "react";

import { useSignalRHub } from "@/contexts/signalr-hub";
import { spaceCapitals } from "@/utils/casing";
import {
    convertTaskInfoUpdateMessageToStoredTask,
    TaskStageViewModel,
} from "@/view-models";
import { ITaskContextData, TasksContext } from "@/contexts/tasks";
import { TaskInfoUpdateMessage } from "@/api/openapi";
import { useToast } from "@/hooks/use-toast";
import { ToastVariants } from "../ui/toast";

const debug: boolean = false;

function logDebug(...data: unknown[]) {
    if (debug) {
        console.debug(...data);
    }
}

let elementId = 0;

/**
 * This component subscribes to SignalR Tasks, and stores them in the DataSourceService store.
 * It will Toast any incoming task message except those of a doNotToastTypes TaskType.
 *
 * Tasks will also be updated in ITaskContextData context, but only if they were previously added to it via createTask.
 * ITaskContextData could be inlined to this component - although that may impact on the ActionButton component's ability to listen to task changes,
 * so an alternative Store may be required.
 *
 * TODO: Another problem with ITaskContextData context is it means This component is recreated whenever it updates Tasks
 * (because updateTask is in the useEffect's dependency list ).
 * This creates multiple duplicate onTaskInfoUpdateMessage SignalR subscriptions. This can be seen by multiple "listener #1"/"#2"/"#3"
 * logs for the same Task msg in console.
 *
 * Simply removing it from the dependency list
 * would mean it would continue to hold a reference to the Initial ITaskContextData, clobbering the array on each new message.
 * @param options
 * @returns
 */

const stageToAppearanceMap: Record<
    TaskStageViewModel,
    ToastVariants["appearance"]
> = {
    Requested: "information",
    Started: "information",
    Complete: "success",
    Failed: "danger",
    Cancelled: "warning",
    Unknown: "information",
};

const TaskService: FunctionComponent<PropsWithChildren> = (
    options,
): ReactElement => {
    const { signalRHub } = useSignalRHub();
    const { getTaskFromId, updateTask } = use<ITaskContextData>(TasksContext);

    const { toast } = useToast();

    useEffect(() => {
        const id = elementId++;
        function onTaskInfoUpdateMessage(message: TaskInfoUpdateMessage) {
            console.log(
                `TaskService onTaskInfoUpdateMessage listener #${id}: `,
                message,
            );
            const taskInfo = convertTaskInfoUpdateMessageToStoredTask(message);

            toast({
                title: `${taskInfo.deviceIdentity}: ${spaceCapitals(taskInfo.taskType || "")}`,
                description: `${taskInfo.stage}${taskInfo.result ? ": " + taskInfo.result : ""}`,
                appearance: stageToAppearanceMap[taskInfo.stage],
                isBold: true,
            });
            const result = getTaskFromId(taskInfo.taskInfoId);
            if (result) {
                updateTask(taskInfo);
            } else {
                logDebug("result not found for task: ", taskInfo);
            }
        }

        async function joinSignalRHandlers() {
            logDebug(`joinSignalR for taskInfoUpdateMessage`);
            if (signalRHub) {
                logDebug(`signalR taskMessageConnected`);
                signalRHub.on("taskInfoUpdateMessage", onTaskInfoUpdateMessage);
            }
        }
        joinSignalRHandlers();
        return () => {
            if (signalRHub) {
                signalRHub.off(
                    "taskInfoUpdateMessage",
                    onTaskInfoUpdateMessage,
                );
            }
        };
    }, [signalRHub, signalRHub?.state, updateTask, getTaskFromId, toast]);

    return <>{options.children}</>;
};

export const TaskServiceComponent = TaskService;
